import React, { useState } from 'react';
import { CreateVerificationDto } from '../../types/inspection';
import { Button } from '../ui/Button';
import { SatisfactionRating } from './SatisfactionRating';
import { Send, X } from 'lucide-react';

interface DepartmentConfirmationFormProps {
  workOrderId: string;
  complaintId: string;
  inspectionId: string;
  inspectionSummary: string;
  onSubmit: (data: CreateVerificationDto) => void;
  onCancel: () => void;
}

export const DepartmentConfirmationForm: React.FC<DepartmentConfirmationFormProps> = ({
  workOrderId,
  complaintId,
  inspectionId,
  inspectionSummary,
  onSubmit,
  onCancel,
}) => {
  const [isSatisfactory, setIsSatisfactory] = useState<boolean | null>(null);
  const [rating, setRating] = useState(0);
  const [comments, setComments] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (isSatisfactory === null) newErrors.satisfactory = 'Select whether work is satisfactory';
    if (rating === 0) newErrors.rating = 'Provide a rating';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onSubmit({
      inspection_id: inspectionId,
      complaint_id: complaintId,
      work_order_id: workOrderId,
      is_satisfactory: isSatisfactory!,
      rating,
      comments,
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div>
          <h3 className="text-base font-semibold text-slate-100">Work Completion Confirmation</h3>
          <p className="text-xs text-slate-400 mt-0.5">Complaint: {complaintId}</p>
        </div>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-200 p-1 rounded-lg hover:bg-slate-800">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 bg-slate-950 rounded-lg border border-slate-800 space-y-2">
        <p className="text-xs font-semibold text-sky-400">Inspection Summary</p>
        <p className="text-xs text-slate-300 leading-relaxed">{inspectionSummary}</p>
      </div>

      <div className="space-y-3">
        <p className="text-xs font-semibold text-slate-300">Is the work satisfactory? *</p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setIsSatisfactory(true)}
            className={`flex-1 p-3 rounded-lg border text-xs font-semibold transition-colors ${
              isSatisfactory === true
                ? 'bg-emerald-500/10 border-emerald-500/50 text-emerald-400'
                : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'
            }`}
          >
            Yes — Satisfactory
          </button>
          <button
            type="button"
            onClick={() => setIsSatisfactory(false)}
            className={`flex-1 p-3 rounded-lg border text-xs font-semibold transition-colors ${
              isSatisfactory === false
                ? 'bg-rose-500/10 border-rose-500/50 text-rose-400'
                : 'bg-slate-950 border-slate-800 text-slate-400 hover:border-slate-600'
            }`}
          >
            No — Unsatisfactory
          </button>
        </div>
        {errors.satisfactory && <p className="text-xs text-rose-400">{errors.satisfactory}</p>}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-300">Rate the work quality *</p>
        <SatisfactionRating rating={rating} onChange={setRating} />
        {errors.rating && <p className="text-xs text-rose-400">{errors.rating}</p>}
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-slate-300">Comments</p>
        <textarea
          value={comments}
          onChange={e => setComments(e.target.value)}
          rows={3}
          placeholder="Any additional comments about the work quality..."
          className="w-full bg-slate-950 border border-slate-800 rounded-lg p-3 text-xs text-slate-200 placeholder-slate-500 focus:border-sky-500 focus:outline-none resize-none"
        />
      </div>

      <div className="flex flex-wrap gap-2 pt-4 border-t border-slate-800 justify-end">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        {isSatisfactory === false && (
          <Button variant="danger" onClick={onCancel} icon={<Send className="w-4 h-4" />}>
            Request Re-inspection
          </Button>
        )}
        <Button variant="primary" onClick={handleSubmit} icon={<Send className="w-4 h-4" />}>
          Confirm & Close
        </Button>
      </div>
    </div>
  );
};
